import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useStore } from '../store/useStore';
import { db } from '../db/db';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEnterAsTab } from '../hooks/useEnterAsTab';
import { INDIAN_STATES } from '../constants/states';
import { AppModal } from '../components/AppModal';
import { validateGstin, decodeGstin } from '../lib/gstValidation';

// ── Group-based section visibility (matching group names in this project) ─────
function getSectionVisibility(groupName: string) {
  const n = groupName.toLowerCase();
  const isSundry    = n.includes('sundry debtor') || n.includes('sundry creditor');
  const isBank      = n.includes('bank account') || isSundry || n.includes('loans');
  const isTax       = isSundry || n.includes('sales account') || n.includes('purchase account') || n.includes('duties');
  const isBillWise  = isSundry;
  return { showParty: isSundry, showBank: isBank, showTax: isTax, showBillByBill: isBillWise };
}

// ── GST type options ──────────────────────────────────────────────────────────
const GST_TYPES = ['Regular', 'Composition', 'Unregistered', 'Consumer', 'Overseas'] as const;
type GstType = typeof GST_TYPES[number];

const Row = ({ label, w = 'w-[250px]', children, top = false }: any) => (
  <div className={`flex ${top ? 'items-start' : 'items-center'} mb-1.5`}>
    <span className={`${w} text-[13px] text-gray-800 flex-shrink-0`}>{label}</span>
    <span className="w-[14px] text-center font-bold text-gray-700 flex-shrink-0">:</span>
    <div className="flex-1 min-w-0">{children}</div>
  </div>
);

export const LedgerCreationView = ({ config, handleItemSelect, ledgerId, onClose, onSave, initialGroupId }: any) => {
  const containerRef  = useRef<HTMLDivElement>(null);
  const nameInputRef  = useRef<HTMLInputElement>(null);
  const isSavingRef   = useRef(false);
  const activeCompany = useStore((state) => state.activeCompany);
  const groups        = useLiveQuery(
    () => activeCompany ? db.groups.where('companyId').equals(activeCompany.id!).toArray() : [],
    [activeCompany]
  );

  // ── Form state ──────────────────────────────────────────────────────────────
  const [name,              setName]              = useState('');
  const [alias,             setAlias]             = useState('');
  const [groupId,           setGroupId]           = useState<number | ''>('');
  const [mailingName,       setMailingName]        = useState('');
  const [address,           setAddress]           = useState('');
  const [state,             setState]             = useState('');
  const [pincode,           setPincode]           = useState('');
  const [billByBill,        setBillByBill]        = useState<'Yes' | 'No'>('Yes');
  const [creditPeriod,      setCreditPeriod]      = useState('');
  const [creditLimit,       setCreditLimit]       = useState('');
  const [allowAdv,          setAllowAdv]          = useState<'Yes' | 'No'>('No');
  const [showBankDetails,   setShowBankDetails]   = useState(false);
  const [bankName,          setBankName]          = useState('');
  const [bankAcct,          setBankAcct]          = useState('');
  const [bankIfsc,          setBankIfsc]          = useState('');
  const [bankBranch,        setBankBranch]        = useState('');
  const [gstRegistrationType, setGstRegistrationType] = useState<GstType>('Regular');
  const [gstin,             setGstin]             = useState('');
  const [pan,               setPan]               = useState('');
  const [openingBalance,    setOpeningBalance]    = useState<number>(0);
  const [balanceType,       setBalanceType]       = useState<'Dr' | 'Cr'>('Dr');
  const [accepting,         setAccepting]         = useState(false);

  useEnterAsTab(containerRef);

  // ── Computed ─────────────────────────────────────────────────────────────────
  const selectedGroup = groups?.find(g => g.id === groupId);
  const vis           = getSectionVisibility(selectedGroup?.name || '');
  const gstinError   = gstin.trim() ? validateGstin(gstin.trim().toUpperCase()) : null;
  const gstinDecoded = gstin.length === 15 && !gstinError
    ? (() => {
        const d = decodeGstin(gstin.toUpperCase());
        return d ? `State: ${d.stateCode} (${d.stateName})  |  PAN: ${d.pan}  |  Entity: ${d.entityType}` : null;
      })()
    : null;

  // ── Load ledger for edit ─────────────────────────────────────────────────────
  useEffect(() => {
    if (ledgerId) {
      db.ledgers.get(ledgerId).then(ledger => {
        if (!ledger) return;
        setName(ledger.name);
        setAlias(ledger.alias || '');
        setGroupId(ledger.groupId ?? '');
        setMailingName((ledger as any).mailingName || ledger.name);
        setAddress(ledger.address || '');
        setState(ledger.state || '');
        setPincode(ledger.pincode || '');
        setBillByBill((ledger as any).billByBill || 'Yes');
        setCreditPeriod((ledger as any).creditPeriod || '');
        setCreditLimit(ledger.creditLimit ? String(ledger.creditLimit) : '');
        setAllowAdv((ledger as any).allowAdv || 'No');
        setShowBankDetails(!!(ledger as any).bankName);
        setBankName((ledger as any).bankName || '');
        setBankAcct((ledger as any).bankAcct || '');
        setBankIfsc((ledger as any).bankIfsc || '');
        setBankBranch((ledger as any).bankBranch || '');
        setGstRegistrationType((ledger.gstRegistrationType as GstType) || 'Regular');
        setGstin(ledger.gstin || '');
        setPan(ledger.pan || '');
        setOpeningBalance(ledger.openingBalance);
        setBalanceType(ledger.balanceType);
      });
    } else {
      // Reset
      setName(''); setAlias(''); setGroupId(''); setMailingName('');
      setAddress(''); setState(''); setPincode('');
      setBillByBill('Yes'); setCreditPeriod(''); setAllowAdv('No');
      setShowBankDetails(false); setBankName(''); setBankAcct(''); setBankIfsc(''); setBankBranch('');
      setGstRegistrationType('Regular'); setGstin(''); setPan('');
      setOpeningBalance(0); setBalanceType('Dr');
    }
  }, [ledgerId]);

  // ── initialGroupId resolution ────────────────────────────────────────────────
  useEffect(() => {
    if (!ledgerId && initialGroupId && groups?.length) {
      const matched = groups.find(g => g.name.toLowerCase() === initialGroupId.toLowerCase());
      if (matched) setGroupId(matched.id!);
    }
  }, [initialGroupId, groups, ledgerId]);

  // Auto-sync mailing name with ledger name (only for new)
  useEffect(() => {
    if (!ledgerId) setMailingName(name);
  }, [name, ledgerId]);

  useEffect(() => {
    if (nameInputRef.current) nameInputRef.current.focus();
  }, [ledgerId]);

  // ── Accept modal keyboard ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!accepting) return;
    const h = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'y' || e.key === 'Enter') { 
        e.preventDefault(); 
        e.stopImmediatePropagation();
        doSave(); 
      }
      else if (e.key.toLowerCase() === 'n' || e.key === 'Escape') { 
        e.preventDefault(); 
        e.stopImmediatePropagation();
        setAccepting(false); 
      }
    };
    window.addEventListener('keydown', h, { capture: true });
    return () => window.removeEventListener('keydown', h, { capture: true });
  }, [accepting]);

  // ── Delete ───────────────────────────────────────────────────────────────────
  const [pendingDelete, setPendingDelete] = useState(false);

  const handleDelete = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!ledgerId) return;

    try {
      const usedCount = await db.voucherEntries.where('ledgerId').equals(ledgerId).count();
      if (usedCount > 0) {
        alert('Ledger is used in vouchers. Cannot delete.');
        return;
      }
      setPendingDelete(true);
    } catch (error: any) {
      alert(error?.message || 'Failed to delete ledger');
    }
  };

  const confirmDelete = async () => {
    setPendingDelete(false);
    if (!ledgerId) return;
    try {
      await db.ledgers.update(ledgerId, {
        isDeleted: true,
        deletedAt: new Date().toISOString(),
        deletedBy: 'System'
      });
      if (onClose) onClose();
      else if (handleItemSelect) handleItemSelect({ name: 'Quit' });
    } catch (error: any) {
      alert(error?.message || 'Failed to delete ledger');
    }
  };

  // ── Global Delete Shortcut ───────────────────────────────────────────────────
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.altKey && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        e.stopPropagation();
        handleDelete();
      }
    };
    if (ledgerId && !accepting) {
      window.addEventListener('keydown', h, { capture: true });
    }
    return () => window.removeEventListener('keydown', h, { capture: true });
  }, [ledgerId, accepting]);

  // ── Save ─────────────────────────────────────────────────────────────────────
  const doSave = async () => {
    setAccepting(false);
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    if (!name.trim() || groupId === '' || !activeCompany) {
      isSavingRef.current = false;
      alert('Name and Group are required');
      return;
    }
    // Validate GSTIN format + checksum if provided
    if (gstin.trim()) {
      const gstinErr = validateGstin(gstin.trim().toUpperCase());
      if (gstinErr) {
        isSavingRef.current = false;
        alert(`Invalid GSTIN: ${gstinErr}`);
        return;
      }
    }
    try {
      const ledgerData: any = {
        name:                name.trim(),
        alias:               alias.trim() || undefined,
        groupId:             Number(groupId),
        openingBalance:      Number(openingBalance) || 0,
        balanceType,
        gstRegistrationType,
        gstin:               gstin.toUpperCase(),
        pan:                 pan.toUpperCase(),
        address,
        state,
        pincode,
        mailingName:         mailingName.trim() || name.trim(),
        billByBill,
        creditPeriod,
        creditLimit: creditLimit ? parseFloat(creditLimit) : undefined,
        allowAdv,
        bankName:            showBankDetails ? bankName : '',
        bankAcct:            showBankDetails ? bankAcct : '',
        bankIfsc:            showBankDetails ? bankIfsc.toUpperCase() : '',
        bankBranch:          showBankDetails ? bankBranch : '',
      };

      if (ledgerId) {
        await db.ledgers.update(ledgerId, ledgerData);
        if (onClose) onClose();
        else if (handleItemSelect) handleItemSelect({ name: 'Quit' });
      } else {
        const exists = await db.ledgers.where('[companyId+name]').equals([activeCompany.id!, name.trim()]).first();
        if (exists) { alert('Already exists'); isSavingRef.current = false; return; }
        const newLedgerId = await db.ledgers.add({
          companyId: activeCompany.id!,
          ...ledgerData,
          isDefault: false,
        });
        if (onSave) {
          onSave({
            id: newLedgerId,
            name: name.trim(),
            alias: alias.trim() || undefined,
            group: groupId,
            openingBalance: Number(openingBalance) || 0,
            balanceType,
            balance: `${(Number(openingBalance) || 0).toFixed(2)} ${balanceType}`
          });
          if (onClose) onClose();
          return;
        }
        // Reset for next entry
        setName(''); setAlias(''); setGroupId(''); setMailingName('');
        setAddress(''); setState(''); setPincode('');
        setBillByBill('Yes'); setCreditPeriod(''); setAllowAdv('No');
        setShowBankDetails(false); setBankName(''); setBankAcct(''); setBankIfsc(''); setBankBranch('');
        setGstRegistrationType('Regular'); setGstin(''); setPan('');
        setOpeningBalance(0); setBalanceType('Dr');
        if (nameInputRef.current) nameInputRef.current.focus();
      }
    } catch (error: any) {
      alert(error?.message || 'Failed to save ledger');
    } finally {
      isSavingRef.current = false;
    }
  };

  const handleSave = () => setAccepting(true);

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const focusNext = useCallback((currentEl: HTMLElement) => {
    const container = containerRef.current;
    if (!container) return;
    const sel = 'input:not([disabled]):not([readonly]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const all = (Array.from(container.querySelectorAll<HTMLElement>(sel)) as HTMLElement[]).filter((el: HTMLElement) => {
      const s = window.getComputedStyle(el);
      return s.display !== 'none' && s.visibility !== 'hidden' && el.offsetParent !== null;
    });
    const idx = all.indexOf(currentEl);
    if (idx !== -1 && idx < all.length - 1) all[idx + 1].focus();
  }, []);

  // ── Yes/No toggle button ──────────────────────────────────────────────────────
  const YesNoBtn = ({ value, onChange }: { value: 'Yes' | 'No'; onChange: (v: 'Yes' | 'No') => void }) => (
    <button
      type="button"
      tabIndex={0}
      role="switch"
      aria-checked={value === 'Yes'}
      className="font-bold cursor-pointer select-none focus:outline-none focus:ring-1 focus:ring-[#1b8086] rounded px-1 border-none bg-transparent font-[inherit] text-[13px]"
      style={{ color: value === 'Yes' ? '#155a15' : '#7a0a0a' }}
      onClick={() => onChange(value === 'Yes' ? 'No' : 'Yes')}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onChange(value === 'Yes' ? 'No' : 'Yes');
          setTimeout(() => focusNext(e.currentTarget), 60);
        }
      }}
    >
      {value}
    </button>
  );


  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm font-sans text-[13px]">
    <div
      ref={containerRef}
      className="flex flex-col bg-white relative overflow-hidden border border-[#444] rounded-sm"
      style={{ width: '1060px', maxWidth: '98vw', height: '94vh' }}
    >
      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      <div className="bg-[#1e8585] text-white flex justify-between px-3 py-1 font-bold shadow-sm z-10 text-[14px] flex-shrink-0">
        <span className="w-1/3 text-left">Ledger {ledgerId ? 'Alteration' : 'Creation'}</span>
        <span className="w-1/3 text-center uppercase">{activeCompany?.name || config?.companyList?.[0]?.name || ''}</span>
        <span
          className="w-1/3 text-right cursor-pointer hover:text-gray-300"
          data-quit-btn
          onClick={() => { if (onClose) onClose(); else if (handleItemSelect) handleItemSelect({ name: 'Quit' }); }}
        >✕</span>
      </div>

      {/* ── TOP STRIP ──────────────────────────────────────────────────────── */}
      <div className="flex border-b border-[#c8d4d4] bg-[#f9fafb] flex-shrink-0">
        <div className="flex-1 flex flex-col p-4 pb-2 border-r border-[#c8d4d4]">
          <div className="flex items-center mb-1">
            <span className="w-[150px] flex-shrink-0">Ledger Name</span>
            <span className="w-[14px] text-center font-bold flex-shrink-0">:</span>
            <input
              ref={nameInputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="flex-1 max-w-[450px] border border-[#a0c5e8] bg-[#fff9c4] px-1 py-[2px] outline-none shadow-inner text-black font-semibold"
              autoFocus
            />
          </div>
          <div className="flex items-center text-[12px]">
            <span className="w-[150px] flex-shrink-0 italic text-gray-500">alias (optional)</span>
            <span className="w-[14px] text-center font-bold flex-shrink-0">:</span>
            <input
              type="text"
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              className="flex-1 max-w-[450px] border-b border-gray-300 bg-transparent px-1 py-[2px] outline-none text-black"
            />
          </div>
        </div>
        <div className="w-[280px] flex flex-col p-4 pb-2 flex-shrink-0">
          <span className="font-bold text-[12px] text-black mb-2">Total Opening Balance</span>
          <span className="font-bold text-[14px] text-black">{openingBalance ? `${openingBalance} ${balanceType}` : ''}</span>
          <span className="text-right text-gray-400 italic text-[11px] mt-auto">Difference</span>
        </div>
      </div>

      {/* ── BODY ───────────────────────────────────────────────────────────── */}
      <div className="flex flex-1 bg-[#fdfdfd] overflow-hidden">

        {/* LEFT COLUMN */}
        <div className="flex-1 flex flex-col p-4 border-r border-dashed border-[#c8d4d4] overflow-y-auto">

          {/* Group Under */}
          <div className="flex items-start mb-4">
            <span className="w-[250px] flex-shrink-0 pt-[3px]">Group Under</span>
            <span className="w-[14px] text-center font-bold flex-shrink-0 pt-[3px]">:</span>
            <div className="flex flex-col flex-1">
              <select
                value={groupId ?? ''}
                onChange={(e) => setGroupId(e.target.value === '' ? '' : Number(e.target.value))}
                className="border border-[#a0c5e8] bg-[#fff9c4] px-1 py-[2px] outline-none shadow-inner text-black font-semibold max-w-[260px] focus:bg-yellow-100"
              >
                <option value="">-- Select Group --</option>
                {groups?.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              {selectedGroup && (
                <span className="text-[11px] mt-0.5 italic text-gray-500">
                  ({selectedGroup.nature})
                </span>
              )}
            </div>
          </div>

          {/* Bill-by-bill section — only for Sundry Debtors/Creditors */}
          {vis.showBillByBill && (
            <div className="mb-1">
              <Row label="Maintain balances bill-by-bill" w="w-[250px]">
                <YesNoBtn value={billByBill} onChange={setBillByBill} />
              </Row>
              {billByBill === 'Yes' && (
                <Row label="Set Default credit period" w="w-[250px]">
                  <input
                    type="text"
                    value={creditPeriod}
                    onChange={(e) => setCreditPeriod(e.target.value)}
                    placeholder="e.g. 30 days"
                    className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-[140px] placeholder-gray-400 focus:bg-yellow-50 focus:border-[#1b8086]"
                  />
                </Row>
              )}
              {billByBill === 'Yes' && (
                <Row label="Credit limit (₹)" w="w-[250px]">
                  <div className="flex flex-col">
                    <input
                      type="number"
                      value={creditLimit}
                      onChange={(e) => setCreditLimit(e.target.value)}
                      placeholder="0 = Unlimited"
                      min={0}
                      className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-[140px] placeholder-gray-400 focus:bg-yellow-50 focus:border-[#1b8086]"
                    />
                    {creditLimit && parseFloat(creditLimit) > 0 && (
                      <span className="text-[10px] text-amber-600 mt-0.5">⚠ Warning shown in voucher when outstanding exceeds ₹{parseFloat(creditLimit).toLocaleString('en-IN')}</span>
                    )}
                  </div>
                </Row>
              )}
              <Row label="Allow advance payments vs invoices" w="w-[250px]">
                <YesNoBtn value={allowAdv} onChange={setAllowAdv} />
              </Row>
            </div>
          )}

          {/* Banking Details */}
          {vis.showBank && (
            <div className="mt-2">
              <div className="font-bold underline underline-offset-2 mb-2 text-black text-[12.5px]">Banking Details</div>
              <Row label="Provide bank details" w="w-[200px]">
                <button
                  type="button"
                  tabIndex={0}
                  role="switch"
                  aria-checked={showBankDetails}
                  className="font-bold cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#1b8086] rounded px-1 border-none bg-transparent font-[inherit] text-[13px]"
                  style={{ color: showBankDetails ? '#155a15' : '#7a0a0a' }}
                  onClick={() => setShowBankDetails(v => !v)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setShowBankDetails(v => !v);
                      setTimeout(() => focusNext(e.currentTarget), 60);
                    }
                  }}
                >
                  {showBankDetails ? 'Yes' : 'No'}
                </button>
              </Row>
              {showBankDetails && (
                <div className="pl-1">
                  <Row label="Bank Name" w="w-[200px]">
                    <input type="text" value={bankName} onChange={e => setBankName(e.target.value)}
                      placeholder="e.g. State Bank of India"
                      className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-full focus:bg-yellow-50" />
                  </Row>
                  <Row label="Account Number" w="w-[200px]">
                    <input type="text" value={bankAcct} onChange={e => setBankAcct(e.target.value)}
                      placeholder="Account No."
                      className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-full focus:bg-yellow-50" />
                  </Row>
                  <Row label="IFSC Code" w="w-[200px]">
                    <input type="text" value={bankIfsc} onChange={e => setBankIfsc(e.target.value.toUpperCase())}
                      placeholder="e.g. SBIN0001234"
                      style={{ letterSpacing: bankIfsc ? '0.4px' : 0 }}
                      className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-full focus:bg-yellow-50" />
                  </Row>
                  <Row label="Branch" w="w-[200px]">
                    <input type="text" value={bankBranch} onChange={e => setBankBranch(e.target.value)}
                      placeholder="Branch / City"
                      className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-full focus:bg-yellow-50" />
                  </Row>
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT COLUMN */}
        <div className="w-[460px] flex flex-col p-4 overflow-y-auto flex-shrink-0">
          <div className="font-bold underline underline-offset-2 mb-3 text-black text-[12.5px]">
            {vis.showParty ? 'Party / Mailing Details' : 'More Details'}
          </div>

          {/* Mailing Name */}
          <Row label="Name" w="w-[120px]">
            <input
              type="text"
              value={mailingName}
              onChange={(e) => setMailingName(e.target.value)}
              className="flex-1 w-full border-b border-gray-300 outline-none text-black font-semibold bg-transparent focus:bg-yellow-50"
            />
          </Row>

          {vis.showParty && (
            <>
              {/* Address — Enter = next field, Shift+Enter = newline */}
              <div className="flex items-start mb-2 mt-1">
                <span className="w-[120px] flex-shrink-0 pt-1 text-[13px]">Address</span>
                <span className="w-[14px] text-center font-bold flex-shrink-0 mt-1">:</span>
                <textarea
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      focusNext(e.currentTarget as HTMLElement);
                    }
                  }}
                  rows={3}
                  className="flex-1 border-b border-gray-300 outline-none text-black h-[56px] resize-none bg-transparent focus:bg-yellow-50"
                  placeholder="Shift+Enter for new line"
                />
              </div>

              <Row label="State" w="w-[120px]">
                <select
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="w-full border-b border-gray-300 outline-none text-black font-semibold bg-transparent focus:bg-yellow-50"
                >
                  <option value="">-- Select State --</option>
                  {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </Row>
              <Row label="Country" w="w-[120px]">
                <span className="text-black">India</span>
              </Row>
              <Row label="Pincode" w="w-[120px]">
                <input
                  type="text"
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value)}
                  maxLength={6}
                  inputMode="numeric"
                  className="w-full border-b border-gray-300 outline-none text-black font-semibold bg-transparent focus:bg-yellow-50"
                />
              </Row>
              <hr className="border-dashed border-[#c8d4d4] my-3" />
            </>
          )}

          {/* Tax Registration */}
          {vis.showTax && (
            <>
              <div className="font-bold underline underline-offset-2 mb-3 text-black text-[12.5px]">Tax Registration Details</div>

              {/* GST Type pills — arrow keys to cycle, Enter to select+move */}
              <div className="flex items-start mb-3">
                <span className="w-[120px] flex-shrink-0 pt-1 text-[13px]">Registration Type</span>
                <span className="w-[14px] text-center font-bold flex-shrink-0 pt-1">:</span>
                <div className="flex flex-wrap gap-1.5 flex-1" role="group" aria-label="GST Registration Type">
                  {GST_TYPES.map((type, i) => (
                    <button
                      key={type}
                      type="button"
                      tabIndex={0}
                      onClick={() => setGstRegistrationType(type)}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowRight') { e.preventDefault(); setGstRegistrationType(GST_TYPES[(i + 1) % GST_TYPES.length]); }
                        else if (e.key === 'ArrowLeft') { e.preventDefault(); setGstRegistrationType(GST_TYPES[(i - 1 + GST_TYPES.length) % GST_TYPES.length]); }
                        else if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setGstRegistrationType(type);
                          // Jump past all pills to next real field
                          const container = containerRef.current;
                          if (!container) return;
                          const lastPill = container.querySelector<HTMLElement>('[role="group"] button:last-child');
                          if (lastPill) focusNext(lastPill);
                        }
                      }}
                      className={`border rounded-full px-2.5 py-[1px] text-[11px] focus:outline-none focus:ring-1 focus:ring-[#1b8086] transition-colors ${
                        gstRegistrationType === type
                          ? 'border-[#1b8086] text-[#1b8086] bg-[#eefcfc] font-semibold'
                          : 'border-gray-300 text-gray-500 bg-white'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* GSTIN with decode hint */}
              <div className="flex items-start mb-3">
                <span className="w-[120px] flex-shrink-0 pt-[3px] text-[13px]">GSTIN / UIN</span>
                <span className="w-[14px] text-center font-bold flex-shrink-0 pt-[3px]">:</span>
                <div className="flex flex-col flex-1">
                  <input
                    type="text"
                    value={gstin}
                    onChange={(e) => setGstin(e.target.value.toUpperCase())}
                    placeholder="e.g. 27AAPFU0939F1ZV"
                    maxLength={15}
                    style={{ letterSpacing: gstin ? '0.5px' : 0 }}
                    className="w-full border-b border-gray-300 outline-none text-black font-semibold focus:bg-yellow-50"
                  />
                  {gstinDecoded && (
                    <span className="text-[10.5px] text-emerald-600 italic mt-0.5">✓ {gstinDecoded}</span>
                  )}
                  {gstinError && gstin.trim() && (
                    <span className="text-[10.5px] text-red-500 mt-0.5">⚠ {gstinError}</span>
                  )}
                </div>
              </div>

              <Row label="PAN / IT No." w="w-[120px]">
                <input
                  type="text"
                  value={pan}
                  onChange={(e) => setPan(e.target.value.toUpperCase())}
                  maxLength={10}
                  style={{ letterSpacing: pan ? '0.5px' : 0 }}
                  className="w-full border-b border-gray-300 outline-none text-black font-semibold focus:bg-yellow-50"
                />
              </Row>
            </>
          )}
        </div>
      </div>

      {/* ── FOOTER ─────────────────────────────────────────────────────────── */}
      <div className="flex justify-between items-center bg-[#ebebeb] border-t border-[#b8b8b8] px-4 py-2 text-[12.5px] flex-shrink-0 h-[33px]">
        <div className="flex items-center font-bold text-black">
          <span>Opening Bal.</span>
          <span className="font-semibold mx-1.5">
            (on {activeCompany?.booksBeginFrom
              ? new Date(activeCompany.booksBeginFrom).toLocaleDateString('en-GB').replace(/\//g, '-')
              : '01-04-2026'}) :
          </span>
          <div className="flex items-center gap-1 border-b border-dashed border-gray-500 pb-[1px]">
            <input
              type="number"
              value={openingBalance || ''}
              onChange={(e) => setOpeningBalance(Number(e.target.value))}
              onFocus={(e) => e.target.select()}
              placeholder="0.00"
              className="w-[110px] bg-transparent outline-none text-right font-normal text-black focus:bg-yellow-50"
            />
            <select
              value={balanceType}
              onChange={(e) => setBalanceType(e.target.value as 'Dr' | 'Cr')}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setAccepting(true); } }}
              className="bg-transparent outline-none cursor-pointer font-bold w-[42px] focus:bg-yellow-50"
            >
              <option value="Dr">Dr</option>
              <option value="Cr">Cr</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <button type="button" onClick={handleSave} tabIndex={-1}
            className="px-4 py-1 bg-[#1e8585] text-white font-bold rounded text-[11.5px] hover:bg-[#166e6e] shadow-sm">
            ✓ Accept (Ctrl+A)
          </button>
          {ledgerId && (
            <button type="button" onClick={(e) => handleDelete(e)} tabIndex={-1}
              className="px-3 py-1 border border-gray-400 text-gray-700 bg-[#e0e0e0] hover:bg-[#d0d0d0] rounded text-[11.5px] font-semibold"
              title="Alt + D to Delete">
              <u>D</u>elete
            </button>
          )}
          <span className="text-gray-500 italic text-[11px]">Enter = Next | Esc = Cancel</span>
        </div>
      </div>

      {/* ── ACCEPT MODAL ───────────────────────────────────────────────────── */}
      {accepting && (
        <div className="absolute inset-0 bg-black/10 z-50 flex items-center justify-center">
          <div className="border-2 border-[#1e8585] bg-white px-16 py-6 shadow-[8px_8px_0_rgba(30,133,133,0.25)] text-center min-w-[200px] rounded-sm">
            <span className="block text-[17px] font-bold mb-4 text-black">Accept ?</span>
            <div className="font-bold text-[15px]">
              <span className="text-[#1e8585] cursor-pointer hover:underline" onClick={doSave}><u>Y</u>es</span>
              &nbsp;&nbsp;or&nbsp;&nbsp;
              <span className="text-gray-500 cursor-pointer hover:underline" onClick={() => setAccepting(false)}><u>N</u>o</span>
            </div>
          </div>
        </div>
      )}

      {/* data-save-btn hidden — triggers accept modal via useEnterAsTab End/Ctrl+A */}
      <button data-save-btn onClick={handleSave} className="hidden" />

      <AppModal
        open={pendingDelete}
        title="Delete Ledger"
        type="warning"
        message="Are you sure you want to delete this ledger?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(false)}
      />
    </div>
    </div>
  );
};
